import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Award,
  BookOpen,
  Building2,
  Calculator,
  CircleUserRound,
  FileText,
  FilePlus2,
  FileQuestion,
  Gift,
  Heart,
  Home,
  Info,
  KeyRound,
  LayoutGrid,
  Newspaper,
  LogOut,
  Phone,
  ScrollText,
  Receipt,
  Sparkles,
  Shield,
  Store,
  Video,
  X,
} from 'lucide-react'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { hasPropertySections, type MegaMenuConfig, type MegaMenuLink } from '@/lib/megaMenu'
import { useMegaMenu } from '@/hooks/useMegaMenu'
import { useUiStore } from '@/store/uiStore'
import { useAuthStore } from '@/store/authStore'
import { useCityStore } from '@/store/cityStore'
import { PATHS } from '@/routes/paths'
import { cn } from '@/lib/utils'

const MENU_SECTIONS = [
  { id: 'buy', label: 'Buy', icon: Home },
  { id: 'rent', label: 'Rent', icon: KeyRound },
  { id: 'commercial', label: 'Commercial', icon: Store },
  { id: 'company', label: 'Company', icon: LayoutGrid },
] as const

type MenuSectionId = (typeof MENU_SECTIONS)[number]['id']

const COMPANY_LINKS = [
  { label: 'QBC Premium', href: PATHS.premium },
  { label: 'All Products', href: PATHS.premiumAllProducts },
  { label: 'Cost Estimator', href: PATHS.calculator },
  { label: 'Real Estate Information', href: PATHS.realEstateBook },
  { label: 'Blog', href: PATHS.blog },
  { label: 'Magazine', href: PATHS.magazine },
  { label: 'Media Center', href: PATHS.media },
  { label: 'Achievements', href: PATHS.achievements },
  { label: 'About Us', href: PATHS.about },
  { label: 'Contact', href: PATHS.contact },
  { label: 'FAQs', href: PATHS.faq },
  { label: 'Privacy Policy', href: PATHS.privacy },
  { label: 'Terms & Conditions', href: PATHS.terms },
]

const MAIN_MENU_LINKS = [
  { label: 'Blog', href: PATHS.blog, icon: Newspaper },
  { label: 'Magazine', href: PATHS.magazine, icon: BookOpen },
  { label: 'Media Center', href: PATHS.media, icon: Video },
  { label: 'QBC Premium', href: PATHS.premium, icon: Gift },
  { label: 'All Products', href: PATHS.premiumAllProducts, icon: LayoutGrid },
  { label: 'Cost Estimator', href: PATHS.calculator, icon: Calculator },
  { label: 'Real Estate Information', href: PATHS.realEstateBook, icon: BookOpen },
  { label: 'About Us', href: PATHS.about, icon: Info },
  { label: 'Achievements', href: PATHS.achievements, icon: Award },
  { label: 'Contact Us', href: PATHS.contact, icon: Phone },
  { label: 'FAQs', href: PATHS.faq, icon: FileText },
  { label: 'Terms & Privacy', href: PATHS.terms, icon: ScrollText },
] as const

const AUTH_QUICK_LINKS = [
  { label: 'Dashboard', href: PATHS.dashboard, icon: LayoutGrid },
  { label: 'Orders & Payments', href: PATHS.myOrders, icon: Receipt },
  { label: 'Wishlist Properties', href: PATHS.myWishlist, icon: Heart },
  { label: 'My Properties', href: PATHS.myProperty, icon: Building2 },
  { label: 'Inquiries', href: PATHS.myInquiry, icon: FileQuestion },
  { label: 'Site Visits', href: PATHS.mySiteVisit, icon: Shield },
] as const

const GUEST_QUICK_LINKS = [
  { label: 'Post Property', href: PATHS.addProperty, icon: FilePlus2 },
  { label: 'QBC Premium', href: PATHS.premium, icon: Shield },
  { label: 'Contact Us', href: PATHS.contact, icon: Phone },
  { label: 'About Us', href: PATHS.about, icon: CircleUserRound },
] as const

function flattenLinks(config: MegaMenuConfig, sectionId?: string): MegaMenuLink[] {
  const sections = sectionId
    ? config.sections.filter((s) => s.id === sectionId)
    : config.sections
  return sections.flatMap((s) => s.columns.flatMap((c) => c.links))
}

export function MobileMenuDrawer() {
  const { isMobileMenuOpen, setMobileMenuOpen, setAuthModalOpen } = useUiStore()
  const { isAuthenticated, user, logout } = useAuthStore()
  const { cityName } = useCityStore()
  const [selectedSection, setActiveSection] = useState<MenuSectionId>('buy')

  const buyMenu = useMegaMenu('buy')
  const rentMenu = useMegaMenu('rent')

  const sectionLinks: Record<MenuSectionId, readonly MegaMenuLink[]> = {
    buy: hasPropertySections(buyMenu) ? flattenLinks(buyMenu) : [],
    rent: hasPropertySections(rentMenu) ? flattenLinks(rentMenu) : [],
    commercial: flattenLinks(buyMenu, 'commercial'),
    company: COMPANY_LINKS,
  }
  const visibleSections = MENU_SECTIONS.filter((s) => sectionLinks[s.id].length > 0)
  const activeSection = visibleSections.some((s) => s.id === selectedSection)
    ? selectedSection
    : (visibleSections[0]?.id ?? 'company')
  const links = sectionLinks[activeSection]
  const quickLinks = isAuthenticated ? AUTH_QUICK_LINKS : GUEST_QUICK_LINKS
  const activeMeta = MENU_SECTIONS.find((s) => s.id === activeSection)
  const sectionHighlights = links.slice(0, 4)

  const close = () => setMobileMenuOpen(false)

  return (
    <Sheet open={isMobileMenuOpen} onOpenChange={setMobileMenuOpen}>
      <SheetContent
        side="full"
        showCloseButton={false}
        showDragHandle={false}
        className="flex flex-col p-0"
      >
        <header className="flex shrink-0 items-center justify-between border-b border-neutral-100 px-4 py-3 safe-top">
          <div>
            <SheetTitle className="text-base">Browse Categories</SheetTitle>
            <p className="text-xs text-neutral-500">in {cityName}</p>
          </div>
          <button
            type="button"
            onClick={close}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-neutral-100 text-neutral-600"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="shrink-0 border-b border-neutral-100 bg-neutral-50/80 px-3 py-2.5">
          <div className="flex gap-1.5 overflow-x-auto hide-scrollbar">
            {visibleSections.map(({ id, label, icon: Icon }) => {
              const active = activeSection === id
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setActiveSection(id)}
                  className={cn(
                    'flex shrink-0 items-center gap-1.5 rounded-full px-3 py-2 text-xs font-semibold transition-colors',
                    active
                      ? 'bg-primary-600 text-white shadow-sm'
                      : 'bg-white text-neutral-600 ring-1 ring-neutral-200',
                  )}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {label}
                </button>
              )
            })}
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3 hide-scrollbar">
          <p className="mb-2.5 text-[11px] font-bold uppercase tracking-wide text-primary-600">
            Quick Access
          </p>
          <div className="mb-4 grid grid-cols-2 gap-2">
            {quickLinks.map(({ label, href, icon: Icon }) => (
              <Link
                key={href}
                to={href}
                onClick={close}
                className="flex items-center gap-2 rounded-xl border border-neutral-200 bg-white px-3 py-2.5 text-xs font-semibold text-neutral-700"
              >
                <Icon className="h-3.5 w-3.5 text-primary-600" />
                {label}
              </Link>
            ))}
          </div>

          <p className="mb-2.5 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-primary-600">
            <Sparkles className="h-3.5 w-3.5" />
            {activeMeta?.label} Highlights
          </p>
          <div className="grid grid-cols-2 gap-2">
            {sectionHighlights.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={close}
                className="rounded-xl border border-neutral-200 bg-white px-3 py-2.5 text-xs font-semibold leading-snug text-neutral-800 shadow-sm transition-all active:scale-[0.99] hover:border-primary-200 hover:bg-primary-50/40"
              >
                {link.label}
              </Link>
            ))}
          </div>

          <p className="mb-2.5 mt-5 text-[11px] font-bold uppercase tracking-wide text-primary-600">
            Main Menus
          </p>
          <div className="mb-5 grid grid-cols-2 gap-2">
            {MAIN_MENU_LINKS.map(({ label, href, icon: Icon }) => (
              <Link
                key={href}
                to={href}
                onClick={close}
                className="flex items-center gap-2 rounded-xl border border-neutral-200 bg-white px-3 py-2.5 text-xs font-semibold text-neutral-700 transition-colors hover:border-primary-200 hover:bg-primary-50/30"
              >
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary-50 text-primary-600">
                  <Icon className="h-3.5 w-3.5" />
                </span>
                <span className="truncate">{label}</span>
              </Link>
            ))}
          </div>

        </div>

        <footer className="shrink-0 border-t border-neutral-100 bg-white px-4 pt-3 pb-[max(1.5rem,env(safe-area-inset-bottom,0px))]">
          {isAuthenticated ? (
            <div className="space-y-2">
              <Link
                to={PATHS.editProfile}
                onClick={close}
                className="flex h-11 items-center justify-center gap-2 rounded-xl bg-primary-50 text-sm font-semibold text-primary-700"
              >
                <CircleUserRound className="h-4 w-4" />
                <span data-clarity-mask="True">{user?.name ?? 'My Account'}</span>
              </Link>
              <button
                type="button"
                onClick={async () => {
                  await logout()
                  close()
                }}
                className="flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-danger-200 bg-danger-50 text-sm font-semibold text-danger-600"
              >
                <LogOut className="h-4 w-4" />
                Logout
              </button>
            </div>
          ) : (
            <Button
              className="h-11 w-full rounded-xl"
              onClick={() => {
                setAuthModalOpen(true)
                close()
              }}
            >
              Login / Register
            </Button>
          )}
        </footer>
      </SheetContent>
    </Sheet>
  )
}
